import type { GridColDef } from "@mui/x-data-grid-premium";
import deepEqual from "fast-deep-equal";
import type { ApiResponse } from "@entrilia/shared/api/types";
import type { StateAction } from "@entrilia/shared/types";
import { distinct, sumBy } from "@entrilia/shared/utilities/arrayHelper";
import type {
  FieldIdToValueMap,
  ObjectCardAutoFill,
  ObjectCardCheck,
  ObjectCardPreview,
  ObjectCardPreviewRequest,
  RelatedObject,
} from "../../api/types/objectTypes";
import type { RelatedObjectValuesState } from "./relatedObjectValuesState";
import { updateRowAction } from "./relatedObjectValuesState";

export interface ObjectCardPreviewConfig {
  getPreview: (objectId: string, request: ObjectCardPreviewRequest) => Promise<ApiResponse<ObjectCardPreview>>;
  fieldIds: string[];
  defaultsTriggerFieldIds: string[];
  relatedObjectType: string;
  relatedFieldIds: string[];
  relatedDefaultsTriggerFieldIds: string[];
  autoFillTooltipsBySource: Record<string, string>;
  onAutoFillOverridden?: (objectId: string, fill: ObjectCardAutoFill, change: AutoFillOverride) => void;
  relatedColumnLayouts?: Record<string, RelatedColumnLayout>;
  getRelatedNoOptionsTexts?: (fieldValues: FieldIdToValueMap) => Record<string, string>;
  summaryDifference?: SummaryDifference;
}

export type RelatedColumnLayout = Pick<GridColDef, "width" | "minWidth" | "flex" | "align" | "headerAlign">;

export interface SummaryDifference {
  label: string;
  amountFieldId: string;
  lineAmountFieldId: string;
}

export interface AutoFillOverride {
  from: string;
  to: string;
}

export type FieldValueLabeler = (fieldId: string, value: unknown) => string;

type CellNotes = Record<string, Record<string, string>>;

export interface RelatedObjectCellDecorations {
  blockerCells: CellNotes;
  warningCells: CellNotes;
  autoFilledCells: CellNotes;
}

export interface ObjectCardPreviewSession {
  fieldValues: FieldIdToValueMap;
  rowValues: Record<string, FieldIdToValueMap>;
  autoFills?: ObjectCardAutoFill[];
  defaultsMissed?: boolean;
}

export const initialObjectCardPreviewSession: ObjectCardPreviewSession = { fieldValues: {}, rowValues: {} };

const normalize = (value: unknown) => (value === "" || value === null ? undefined : value);

const isSameValue = (a: unknown, b: unknown) => deepEqual(normalize(a), normalize(b));

const pick = (values: FieldIdToValueMap, fieldIds: string[]): FieldIdToValueMap =>
  Object.fromEntries(fieldIds.filter((id) => id in values).map((id) => [id, values[id]]));

const fallbackAutoFillTooltip = "Filled in automatically.";

const getAutoFillSources = (fills: ObjectCardAutoFill[], values: FieldIdToValueMap): Record<string, string> =>
  Object.fromEntries(
    fills
      .filter((fill) => isSameValue(values[fill.fieldId], fill.value))
      .flatMap((fill) => (fill.source === undefined ? [] : [[fill.fieldId, fill.source]]))
  );

export const getObjectCardPreviewRequest = (
  config: ObjectCardPreviewConfig,
  session: ObjectCardPreviewSession,
  storedFieldValues: FieldIdToValueMap,
  fieldValues: FieldIdToValueMap,
  storedRows: RelatedObject[],
  state: RelatedObjectValuesState
): ObjectCardPreviewRequest => {
  const rows = state.rowsByObjectType[config.relatedObjectType] ?? [];
  const defaultsForFieldIds = config.defaultsTriggerFieldIds.filter((id) => {
    const appliedFor = id in session.fieldValues ? session.fieldValues[id] : storedFieldValues[id];
    return !isSameValue(fieldValues[id], appliedFor);
  });
  const autoFillsToResend = defaultsForFieldIds.length > 0 ? session.autoFills : undefined;

  return {
    fieldValuesUserInput: pick(fieldValues, config.fieldIds),
    defaultsForFieldIds,
    relatedObjects: rows
      .filter((row) => row.rowState.updateStatus !== undefined)
      .map((row) => {
        const appliedFor = session.rowValues[row.id] ?? storedRows.find((r) => r.id === row.id)?.fieldValues ?? {};
        const defaultsRequested = config.relatedDefaultsTriggerFieldIds.some(
          (id) => !isSameValue(row[id], appliedFor[id])
        );
        return {
          key: row.id,
          objectId: row.rowState.updateStatus === "new" ? undefined : row.id,
          fieldValuesUserInput: pick(row, config.relatedFieldIds),
          defaultsRequested,
          defaultsAppliedFor: defaultsRequested ? pick(appliedFor, config.relatedDefaultsTriggerFieldIds) : undefined,
          ...(autoFillsToResend && {
            autoFillSources: getAutoFillSources(
              autoFillsToResend.filter((fill) => fill.relatedObjectKey === row.id),
              row
            ),
          }),
        };
      }),
    deletedRelatedObjectIds: [...(state.objectIdsToDeleteByObjectType[config.relatedObjectType] ?? [])],
    ...(autoFillsToResend && {
      autoFillSources: getAutoFillSources(
        autoFillsToResend.filter((fill) => fill.relatedObjectKey === undefined),
        fieldValues
      ),
    }),
  };
};

export const requestsDefaults = (request: ObjectCardPreviewRequest): boolean =>
  request.defaultsForFieldIds.length > 0 || request.relatedObjects.some((x) => x.defaultsRequested);

export const acknowledgeDefaultsAction =
  (
    config: ObjectCardPreviewConfig,
    request: ObjectCardPreviewRequest,
    preview: ObjectCardPreview
  ): StateAction<ObjectCardPreviewSession> =>
  (session) => {
    if (!requestsDefaults(request)) {
      return session;
    }

    const filledFieldValues = Object.fromEntries(
      preview.defaults.filter((d) => d.relatedObjectKey === undefined).map((d) => [d.fieldId, d.value])
    );

    return {
      ...session,
      fieldValues: {
        ...session.fieldValues,
        ...pick(request.fieldValuesUserInput, request.defaultsForFieldIds),
        ...pick(filledFieldValues, config.defaultsTriggerFieldIds),
      },
      rowValues: {
        ...session.rowValues,
        ...Object.fromEntries(
          request.relatedObjects
            .filter((x) => x.defaultsRequested)
            .map((x) => [x.key, pick(x.fieldValuesUserInput, config.relatedDefaultsTriggerFieldIds)])
        ),
      },
    };
  };

export const rememberAutoFillsAction =
  (preview: ObjectCardPreview): StateAction<ObjectCardPreviewSession> =>
  (session) => {
    const isFilledAgain = (earlier: ObjectCardAutoFill) =>
      [...preview.autoFilled, ...preview.defaults].some(
        (fill) => fill.relatedObjectKey === earlier.relatedObjectKey && fill.fieldId === earlier.fieldId
      );
    const autoFills = [...(session.autoFills ?? []).filter((fill) => !isFilledAgain(fill)), ...preview.autoFilled];
    return deepEqual(session.autoFills, autoFills) ? session : { ...session, autoFills };
  };

export const giveUpDefaultsAction =
  (config: ObjectCardPreviewConfig, request: ObjectCardPreviewRequest): StateAction<ObjectCardPreviewSession> =>
  (session) => {
    if (!requestsDefaults(request)) {
      return session;
    }

    const noPreview: ObjectCardPreview = { fieldValues: {}, defaults: [], autoFilled: [], checks: [] };

    return { ...acknowledgeDefaultsAction(config, request, noPreview)(session), defaultsMissed: true };
  };

export const getFieldDefaults = (
  request: ObjectCardPreviewRequest,
  preview: ObjectCardPreview,
  fieldValues: FieldIdToValueMap
): FieldIdToValueMap =>
  Object.fromEntries(
    preview.defaults
      .filter(
        (d) =>
          d.relatedObjectKey === undefined &&
          isSameValue(fieldValues[d.fieldId], request.fieldValuesUserInput[d.fieldId])
      )
      .filter((d) => !isSameValue(fieldValues[d.fieldId], d.value))
      .map((d) => [d.fieldId, d.value ?? ""])
  );

export const applyRowDefaultsAction =
  (
    config: ObjectCardPreviewConfig,
    request: ObjectCardPreviewRequest,
    preview: ObjectCardPreview,
    storedRows: RelatedObject[]
  ): StateAction<RelatedObjectValuesState> =>
  (state) =>
    preview.defaults.reduce((current, fill) => {
      const row = current.rowsByObjectType[config.relatedObjectType]?.find((r) => r.id === fill.relatedObjectKey);
      if (!row) {
        return current;
      }

      const sentValues =
        request.relatedObjects.find((x) => x.key === row.id)?.fieldValuesUserInput ??
        storedRows.find((r) => r.id === row.id)?.fieldValues ??
        {};
      if (!isSameValue(row[fill.fieldId], sentValues[fill.fieldId]) || isSameValue(row[fill.fieldId], fill.value)) {
        return current;
      }

      return updateRowAction(config.relatedObjectType, { ...row, [fill.fieldId]: fill.value ?? "" })(current);
    }, state);

const getTooltipsOfUnchangedFills = (
  autoFilled: ObjectCardAutoFill[],
  values: FieldIdToValueMap,
  tooltipsBySource: Record<string, string>
): Record<string, string> =>
  Object.fromEntries(
    autoFilled
      .filter((fill) => isSameValue(values[fill.fieldId], fill.value))
      .map((fill) => [fill.fieldId, tooltipsBySource[fill.source ?? ""] ?? fallbackAutoFillTooltip])
  );

export const getAutoFillsOverriddenByEdit = (
  autoFilled: ObjectCardAutoFill[],
  relatedObjectKey: string | undefined,
  oldValues: FieldIdToValueMap,
  newValues: FieldIdToValueMap
): ObjectCardAutoFill[] =>
  autoFilled.filter(
    (fill) =>
      fill.relatedObjectKey === relatedObjectKey &&
      isSameValue(oldValues[fill.fieldId], fill.value) &&
      !isSameValue(newValues[fill.fieldId], fill.value)
  );

export const getFieldsAutoFillTooltips = (
  config: ObjectCardPreviewConfig,
  preview: ObjectCardPreview,
  fieldValues: FieldIdToValueMap
): Record<string, string> =>
  getTooltipsOfUnchangedFills(
    preview.autoFilled.filter((fill) => fill.relatedObjectKey === undefined),
    fieldValues,
    config.autoFillTooltipsBySource
  );

export const getActionCheckMessages = (checks: ObjectCardCheck[]): { blockers: string[]; warnings: string[] } => ({
  blockers: distinct(checks.filter((check) => check.isBlocker).map((check) => check.message)),
  warnings: distinct(
    checks.filter((check) => !check.isBlocker && check.relatedObjectKey === undefined).map((check) => check.message)
  ),
});

export const getCellDecorations = (
  config: ObjectCardPreviewConfig,
  preview: ObjectCardPreview,
  state: RelatedObjectValuesState
): RelatedObjectCellDecorations => {
  const getCellNotes = (isBlocker: boolean) =>
    preview.checks
      .filter((check) => check.relatedObjectKey !== undefined && check.isBlocker === isBlocker)
      .reduce<CellNotes>((cells, check) => {
        const rowId = check.relatedObjectKey ?? "";
        const columnId = check.additionalFieldId ? `${check.fieldId}.${check.additionalFieldId}` : check.fieldId;
        cells[rowId] = { ...cells[rowId], [columnId]: check.message };
        return cells;
      }, {});

  const rows = state.rowsByObjectType[config.relatedObjectType] ?? [];

  return {
    blockerCells: getCellNotes(true),
    warningCells: getCellNotes(false),
    autoFilledCells: Object.fromEntries(
      rows.map((row) => [
        row.id,
        getTooltipsOfUnchangedFills(
          preview.autoFilled.filter((fill) => fill.relatedObjectKey === row.id),
          row,
          config.autoFillTooltipsBySource
        ),
      ])
    ),
  };
};

export const getLinesDifference = (amount: unknown, lineAmounts: unknown[]): number | undefined => {
  if (typeof amount !== "number") {
    return undefined;
  }

  const linesTotal = sumBy(lineAmounts, (lineAmount) => (typeof lineAmount === "number" ? lineAmount : 0));
  const differenceInCents = Math.round((linesTotal - amount) * 100);
  return differenceInCents === 0 ? undefined : differenceInCents / 100;
};
